import { Avatar, AvatarFallback } from "@bowfast/ui/components/avatar";
import type { BadgeProps } from "@bowfast/ui/components/badge";
import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardFooter, CardHeader } from "@bowfast/ui/components/card";
import { Input } from "@bowfast/ui/components/input";
import { Label } from "@bowfast/ui/components/label";
import { Separator } from "@bowfast/ui/components/separator";
import { Switch } from "@bowfast/ui/components/switch";
import { cn } from "@bowfast/ui/lib/utils";
import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  AnchorIcon,
  BellIcon,
  BriefcaseIcon,
  CreditCardIcon,
  KeyRoundIcon,
  LockIcon,
  MapPinIcon,
  PenLineIcon,
  SettingsIcon,
  ShieldCheckIcon,
  TrashIcon,
  UploadIcon,
  UserIcon,
  UsersIcon,
} from "lucide-react";
import { useState } from "react";

// ── Animation ─────────────────────────────────────────────────────────────────

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, ease: EASE_OUT },
};

const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.2 },
};

// ── Types ─────────────────────────────────────────────────────────────────────

type Section =
  | "org-profile"
  | "org-crew"
  | "org-notifications"
  | "org-danger"
  | "user-profile"
  | "user-notifications"
  | "user-security"
  | "user-danger";

type OrgRole = "owner" | "admin" | "skipper" | "instructor" | "crew";

interface OrgMember {
  id: string;
  name: string;
  initials: string;
  email: string;
  role: OrgRole;
  gradient: string;
  joinedDate: string;
}

interface NavItem {
  id: Section;
  label: string;
  Icon: LucideIcon;
  danger?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

// ── Config ────────────────────────────────────────────────────────────────────

const ORG_ROLE_CONFIG: Record<OrgRole, { label: string; variant: BadgeProps["variant"] }> = {
  owner: { label: "Owner", variant: "charter" },
  admin: { label: "Admin", variant: "confirmed" },
  skipper: { label: "Skipper", variant: "active" },
  instructor: { label: "Instructor", variant: "planning" },
  crew: { label: "Crew", variant: "secondary" },
};

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Organisation",
    items: [
      { id: "org-profile", label: "Profile", Icon: BriefcaseIcon },
      { id: "org-crew", label: "Crew & admins", Icon: UsersIcon },
      { id: "org-notifications", label: "Notifications", Icon: BellIcon },
      { id: "org-danger", label: "Danger zone", Icon: TrashIcon, danger: true },
    ],
  },
  {
    label: "My account",
    items: [
      { id: "user-profile", label: "Profile", Icon: UserIcon },
      { id: "user-notifications", label: "Notifications", Icon: BellIcon },
      { id: "user-security", label: "Security", Icon: LockIcon },
      { id: "user-danger", label: "Danger zone", Icon: TrashIcon, danger: true },
    ],
  },
];

// ── Mock data ─────────────────────────────────────────────────────────────────

const ORG_MEMBERS: OrgMember[] = [
  {
    id: "1",
    name: "Sofia Carvalho",
    initials: "SC",
    email: "sofia@bluewater.co",
    role: "owner",
    gradient: "from-violet-500 to-fuchsia-500",
    joinedDate: "Jan 2024",
  },
  {
    id: "2",
    name: "Nuno Ferreira",
    initials: "NF",
    email: "nuno@bluewater.co",
    role: "admin",
    gradient: "from-abyss-500 to-abyss-300",
    joinedDate: "Mar 2024",
  },
  {
    id: "3",
    name: "Ana Martins",
    initials: "AM",
    email: "ana@bluewater.co",
    role: "skipper",
    gradient: "from-shallow-500 to-shallow-300",
    joinedDate: "Jun 2024",
  },
  {
    id: "4",
    name: "Carlos Ribeiro",
    initials: "CR",
    email: "carlos@bluewater.co",
    role: "instructor",
    gradient: "from-signal-500 to-signal-300",
    joinedDate: "Sep 2024",
  },
  {
    id: "5",
    name: "Beatriz Santos",
    initials: "BS",
    email: "beatriz@bluewater.co",
    role: "crew",
    gradient: "from-coral-400 to-coral-300",
    joinedDate: "Nov 2024",
  },
];

// ── Shared sub-components ─────────────────────────────────────────────────────

function SectionHeader({
  Icon,
  title,
  description,
}: Readonly<{ Icon: LucideIcon; title: string; description: string }>) {
  return (
    <div className="flex items-start gap-3 pb-4">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-abyss-50 dark:bg-abyss-800">
        <Icon className="size-4 text-abyss-500" />
      </div>
      <div>
        <h2 className="font-heading text-[0.9375rem] font-semibold">{title}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function ToggleRow({
  title,
  description,
  defaultChecked = false,
}: Readonly<{ title: string; description: string; defaultChecked?: boolean }>) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch defaultChecked={defaultChecked} className="shrink-0" />
    </div>
  );
}

// ── Section panels ────────────────────────────────────────────────────────────

function OrgProfilePanel() {
  return (
    <Card>
      <CardHeader className="pb-4">
        <SectionHeader
          Icon={BriefcaseIcon}
          title="Organisation profile"
          description="Your organisation's public-facing name, description, and contact details"
        />
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center gap-5">
          <div className="flex size-16 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-abyss-500 to-abyss-300">
            <AnchorIcon className="size-7 text-white" />
          </div>
          <div>
            <Button variant="outline" size="sm" className="gap-2">
              <UploadIcon className="size-3.5" />
              Upload logo
            </Button>
            <p className="mt-1.5 text-xs text-muted-foreground">PNG or SVG · 512×512 px recommended</p>
          </div>
        </div>
        <Separator />
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Organisation name</Label>
            <Input defaultValue="Bluewater Sailing Co." placeholder="Your organisation name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Slug</Label>
            <p className="text-xs text-muted-foreground">Used in public URLs — e.g. bowfast.io/bluewater</p>
            <div className="flex items-center overflow-hidden rounded-md border border-input focus-within:ring-2 focus-within:ring-ring/20">
              <span className="flex h-9 select-none items-center border-r border-input bg-muted px-3 text-sm text-muted-foreground">
                bowfast.io/
              </span>
              <Input
                defaultValue="bluewater"
                className="rounded-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Home port</Label>
            <div className="relative">
              <MapPinIcon className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input defaultValue="Lisbon, Portugal" className="pl-8" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Description</Label>
            <textarea
              className="h-20 w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20"
              defaultValue="Offshore passage-making school and expedition organiser based in Lisbon."
            />
          </div>
        </div>
      </CardContent>
      <CardFooter className="justify-end border-t pt-4">
        <Button size="sm">Save changes</Button>
      </CardFooter>
    </Card>
  );
}

function OrgCrewPanel() {
  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-4">
          <SectionHeader
            Icon={UsersIcon}
            title="Crew & admins"
            description="Manage who has access to your organisation and their roles"
          />
          <Button size="sm" className="shrink-0 gap-2">
            <UsersIcon className="size-3.5" />
            Invite crew
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="divide-y divide-border">
          {ORG_MEMBERS.map((member) => {
            const roleCfg = ORG_ROLE_CONFIG[member.role];
            return (
              <div key={member.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                <div className={cn("shrink-0 rounded-full bg-linear-to-br p-[1.5px]", member.gradient)}>
                  <Avatar size="sm" className="border-2 border-card">
                    <AvatarFallback className="bg-abyss-900 text-xs font-bold text-white">
                      {member.initials}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold">{member.name}</p>
                    <Badge variant={roleCfg.variant} className="text-[0.625rem]">
                      {roleCfg.label}
                    </Badge>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {member.email} · Joined {member.joinedDate}
                  </p>
                </div>
                {member.role !== "owner" && (
                  <Button variant="ghost" size="sm" className="shrink-0 gap-1.5 text-xs text-muted-foreground">
                    <PenLineIcon className="size-3" />
                    Edit
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function OrgNotificationsPanel() {
  return (
    <Card>
      <CardHeader className="pb-4">
        <SectionHeader
          Icon={BellIcon}
          title="Organisation notifications"
          description="Choose which events trigger emails to organisation admins"
        />
      </CardHeader>
      <CardContent className="divide-y divide-border pt-0">
        <ToggleRow
          title="New join request"
          description="Send an email when a sailor requests to join an expedition"
          defaultChecked
        />
        <ToggleRow
          title="Expedition status change"
          description="Notify admins when an expedition moves to Active or Completed"
          defaultChecked
        />
        <ToggleRow
          title="Certificate issued"
          description="Notify admins each time a certificate is issued to a crew member"
        />
        <ToggleRow
          title="New crew registration"
          description="Alert admins when a new crew member accepts an invitation"
          defaultChecked
        />
      </CardContent>
    </Card>
  );
}

function OrgDangerPanel() {
  return (
    <Card className="border-destructive/30">
      <CardHeader className="pb-4">
        <SectionHeader
          Icon={ShieldCheckIcon}
          title="Danger zone"
          description="Irreversible actions — proceed with care"
        />
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center justify-between gap-4 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3">
          <div>
            <p className="text-sm font-semibold">Delete organisation</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Permanently removes all data — expeditions, crew, certificates, vessels
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 gap-2 border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground"
          >
            <TrashIcon className="size-3.5" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function UserProfilePanel() {
  return (
    <Card>
      <CardHeader className="pb-4">
        <SectionHeader
          Icon={UserIcon}
          title="Your profile"
          description="How you appear to skippers and crew on expeditions"
        />
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center gap-5">
          <div className="shrink-0 rounded-full bg-linear-to-br from-violet-500 to-fuchsia-500 p-0.5">
            <Avatar className="size-14 border-[3px] border-card">
              <AvatarFallback className="bg-abyss-900 font-heading text-base font-bold text-white">SC</AvatarFallback>
            </Avatar>
          </div>
          <div>
            <Button variant="outline" size="sm" className="gap-2">
              <UploadIcon className="size-3.5" />
              Change photo
            </Button>
            <p className="mt-1.5 text-xs text-muted-foreground">JPG or PNG · 400×400 px recommended</p>
          </div>
        </div>
        <Separator />
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">First name</Label>
              <Input defaultValue="Sofia" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Last name</Label>
              <Input defaultValue="Carvalho" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Display name</Label>
            <p className="text-xs text-muted-foreground">Shown on expedition crew lists</p>
            <Input defaultValue="Sofia Carvalho" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Email</Label>
            <Input type="email" defaultValue="sofia@bluewater.co" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Phone</Label>
              <Input type="tel" defaultValue="+351 912 345 678" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Home port</Label>
              <div className="relative">
                <MapPinIcon className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input defaultValue="Lisbon, Portugal" className="pl-8" />
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Bio</Label>
            <p className="text-xs text-muted-foreground">A short introduction for fellow crew</p>
            <textarea
              className="h-20 w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20"
              defaultValue="Ocean-going skipper with 12 years of offshore experience. Specialises in Atlantic crossings."
            />
          </div>
        </div>
      </CardContent>
      <CardFooter className="justify-end border-t pt-4">
        <Button size="sm">Save profile</Button>
      </CardFooter>
    </Card>
  );
}

function UserNotificationsPanel() {
  return (
    <Card>
      <CardHeader className="pb-4">
        <SectionHeader
          Icon={BellIcon}
          title="Notification preferences"
          description="Choose when Bowfast sends you emails and in-app alerts"
        />
      </CardHeader>
      <CardContent className="divide-y divide-border pt-0">
        <ToggleRow
          title="Expedition invitations"
          description="When a skipper invites you to join an expedition"
          defaultChecked
        />
        <ToggleRow
          title="Join request updates"
          description="When your request to join an expedition is approved or rejected"
          defaultChecked
        />
        <ToggleRow
          title="Certificate ready"
          description="When a new certificate is issued on your profile"
          defaultChecked
        />
        <ToggleRow
          title="Expedition reminders"
          description="48-hour reminder before your departure date"
          defaultChecked
        />
        <ToggleRow title="New skill assessments" description="When an instructor records a skill assessment for you" />
      </CardContent>
    </Card>
  );
}

function UserSecurityPanel() {
  return (
    <Card>
      <CardHeader className="pb-4">
        <SectionHeader
          Icon={LockIcon}
          title="Security"
          description="Manage authentication and access to your account"
        />
      </CardHeader>
      <CardContent className="space-y-0 divide-y divide-border pt-0">
        <div className="flex items-center justify-between gap-4 py-4 first:pt-0">
          <div>
            <div className="flex items-center gap-2">
              <KeyRoundIcon className="size-3.5 text-muted-foreground" />
              <p className="text-sm font-medium">Password</p>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">Last changed 3 months ago</p>
          </div>
          <Button variant="outline" size="sm">
            Change password
          </Button>
        </div>
        <div className="flex items-center justify-between gap-4 py-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="size-3.5 text-muted-foreground" />
              <p className="text-sm font-medium">Two-factor authentication</p>
              <Badge variant="secondary" className="text-[0.625rem]">
                Off
              </Badge>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">Adds a second layer of protection on sign-in</p>
          </div>
          <Button variant="outline" size="sm">
            Enable
          </Button>
        </div>
        <div className="flex items-center justify-between gap-4 py-4 last:pb-0">
          <div>
            <div className="flex items-center gap-2">
              <CreditCardIcon className="size-3.5 text-muted-foreground" />
              <p className="text-sm font-medium">Active sessions</p>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">1 session active · Chrome on macOS</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
          >
            Sign out all
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function UserDangerPanel() {
  return (
    <Card className="border-destructive/30">
      <CardHeader className="pb-4">
        <SectionHeader
          Icon={ShieldCheckIcon}
          title="Danger zone"
          description="Irreversible actions — proceed with care"
        />
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center justify-between gap-4 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3">
          <div>
            <p className="text-sm font-semibold">Delete account</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Permanently removes your profile, sea miles log, and certificates
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 gap-2 border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground"
          >
            <TrashIcon className="size-3.5" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Section router ────────────────────────────────────────────────────────────

function SectionPanel({ section }: Readonly<{ section: Section }>) {
  switch (section) {
    case "org-profile":
      return <OrgProfilePanel />;
    case "org-crew":
      return <OrgCrewPanel />;
    case "org-notifications":
      return <OrgNotificationsPanel />;
    case "org-danger":
      return <OrgDangerPanel />;
    case "user-profile":
      return <UserProfilePanel />;
    case "user-notifications":
      return <UserNotificationsPanel />;
    case "user-security":
      return <UserSecurityPanel />;
    case "user-danger":
      return <UserDangerPanel />;
  }
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function SettingsPage() {
  const [activeSection, setActiveSection] = useState<Section>("org-profile");

  return (
    <div className="mx-auto w-full max-w-350 space-y-5 p-4 md:p-6">
      {/* Page header */}
      <motion.div {...fadeUp} className="flex items-center gap-3">
        <div className="flex size-11 items-center justify-center rounded-xl bg-abyss-50 dark:bg-abyss-800">
          <SettingsIcon className="size-5 text-abyss-500" />
        </div>
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight md:text-[1.375rem]">Settings</h1>
          <p className="text-sm text-muted-foreground">Manage your organisation and personal preferences</p>
        </div>
      </motion.div>

      {/* Two-column layout */}
      <motion.div
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.04 }}
        className="flex flex-col gap-5 lg:flex-row lg:items-start"
      >
        {/* ── Left nav card ── */}
        <Card className="w-full shrink-0 lg:w-56">
          <CardContent className="p-2">
            {NAV_GROUPS.map((group, gi) => (
              <div key={group.label}>
                {gi > 0 && <Separator className="my-2" />}
                <p className="mb-1 px-2 pt-1 text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                  {group.label}
                </p>
                {group.items.map((item) => {
                  const isActive = activeSection === item.id;

                  let itemClass: string;
                  if (isActive && item.danger) {
                    itemClass = "bg-destructive/10 text-destructive";
                  } else if (isActive) {
                    itemClass = "bg-abyss-50 text-abyss-600 dark:bg-abyss-800 dark:text-abyss-300";
                  } else if (item.danger) {
                    itemClass = "text-destructive/70 hover:bg-destructive/5 hover:text-destructive";
                  } else {
                    itemClass = "text-muted-foreground hover:bg-muted hover:text-foreground";
                  }

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setActiveSection(item.id)}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors",
                        itemClass,
                      )}
                    >
                      <item.Icon className="size-3.5 shrink-0" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* ── Right content ── */}
        <div className="min-w-0 flex-1">
          <motion.div key={activeSection} {...fadeIn}>
            <SectionPanel section={activeSection} />
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
